import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import DOMPurify from "dompurify";
import baseURL from "../../utils/BaseURL";

// Static Assets
import phoneImg from "../../images/home/form/phone.png";
import emailImg from "../../images/home/form/email.png";
import location from "../../images/home/form/location.png";
import insta from "../../images/home/form/instagram.png";
import face from "../../images/home/form/facebook.png";

const Form = ({ data }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subject: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (formData) => {
      const body = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        subjects: formData.subject,
        message: formData.message,
        phone: formData.phone,
        email: formData.email,
      };
      return axios.post(`${baseURL}/api/contact`, body, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
    },
    onSuccess: () => {
      reset(); 
    },
  });

  const onSubmit = (formData) => mutation.mutate(formData);

  // Helper for Input Styling
  const getInputClass = (error) =>
    `bg-white focus:outline-none border-b-[0.1rem] text-sm md:text-xl pb-2 pl-0 h-12 transition-colors ${
      error ? "border-b-[#f55389]" : "border-b-[#8D8D8D]"
    }`;

  return (
    <div
      id="contact"
      className="rounded-2xl mx-5 md:mx-14 mt-[12%] sm:-mt-8 md:-mt-24 mb-5 md:h-[50rem] bg-white md:grid md:grid-cols-3 flex flex-col-reverse"
    >
      <div className="m-3 rounded-xl h-[96%] bg-[#15363A] relative flex flex-col justify-between p-5 sm:p-10 overflow-hidden">
        <div className="flex flex-col gap-10 md:gap-20">
          <div>
            <h1 className="text-xs sm:text-xl md:text-4xl font-semibold text-white">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(data.title),
                }}
              />
            </h1>
            <h3 className="text-xs font-semibold md:text-xl hidden sm:flex pt-4 text-[#C9C9C9]">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(data.desc),
                }}
              />
            </h3>
          </div>

          <div className="flex flex-col gap-4 md:gap-5 font-semibold text-xs md:text-lg text-[#C9C9C9]">
            <ContactItem
              icon={phoneImg}
              text={data.phone}
              link={`tel:${data.phone}`}
            />
            <ContactItem
              icon={emailImg}
              text={data.email}
              link={`mailto:${data.email}`}
            />
            <div className="flex gap-1 sm:gap-6">
              <img
                className="md:w-[1.5rem] w-[1.2rem] h-[1rem] md:h-[1.6rem]"
                src={location}
                alt="location"
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(data.address),
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 z-30">
          <SocialLink href={data.facebook} icon={face} alt="facebook" />
          <SocialLink href={data.instagram} icon={insta} alt="instagram" />
        </div>

        {/* Background Decorative Circles */}
        <div className="z-20 absolute rounded-full w-24 h-24 md:w-36 md:h-36 bg-[#fff9f94f] opacity-45 bottom-6 right-4 md:bottom-16 md:right-14"></div>
        <div className="z-21 absolute rounded-full w-48 h-48 md:w-72 md:h-72 bg-[#fff9f94f] opacity-45 -bottom-14 -right-14 md:-bottom-28 md:-right-24"></div>
      </div>

      {/* Main Form Section */}
      <div className="col-span-2 pt-3 md:pt-32 pl-4 sm:pl-12 md:pl-32 pr-4 sm:pr-10">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col text-[#8D8D8D]"
        >
          <h1 className="flex md:hidden pl-4 text-lg sm:text-2xl md:text-5xl text-[#15363A] pt-5 font-bold">
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data.bigTitle),
              }}
            />
          </h1>

          {/* Status Message */}
          {mutation.isError && (
            <p className="text-[#f55389] text-center font-bold">
              An error occurred, please try again.
            </p>
          )}
          {mutation.isSuccess && (
            <p className="text-[#998147] text-center font-bold">
              Message sent successfully!
            </p>
          )}

          <div className="flex md:flex-row flex-col">
            <FormInput
              label="First Name"
              name="firstName"
              register={register}
              validation={{ required: "First name required" }}
              error={errors.firstName}
              className={getInputClass(errors.firstName)}
            />
            <FormInput
              label="Last Name"
              name="lastName"
              register={register}
              validation={{ required: "Last name required" }}
              error={errors.lastName}
              className={getInputClass(errors.lastName)}
            />
          </div>

          <div className="flex md:flex-row flex-col">
            <FormInput
              label="Email"
              name="email"
              type="email"
              register={register}
              validation={{
                required: "Email required",
                pattern: { value: /^\S+@\S+$/i, message: "Invalid email" },
              }}
              error={errors.email}
              className={getInputClass(errors.email)}
            />
            <FormInput
              label="Phone"
              name="phone"
              register={register}
              validation={{ required: "Phone required" }}
              error={errors.phone}
              className={getInputClass(errors.phone)}
            />
          </div>

          {/* Subjects Selection */}
          <div className="flex flex-col pl-4 pt-4 pb-4">
            <h3
              className={`text-sm md:text-xl font-bold ${errors.subject ? "text-[#f55389]" : "text-[#011C2A]"}`}
            >
              Select Subject ?
            </h3>
            <div className="flex md:flex-row flex-col justify-start md:gap-12 pt-8 text-[#011C2A] font-semibold">
              {["general", "support", "sales", "feedback"].map((sub) => (
                <div key={sub} className="flex gap-3 items-center">
                  <input
                    type="radio"
                    value={sub}
                    {...register("subject", { required: true })}
                    className="accent-[#998147]"
                  />
                  <label className="capitalize hover:text-[#998147] cursor-pointer text-xs md:text-lg">
                    {sub}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <FormInput
            label="Message"
            name="message"
            register={register}
            validation={{ required: "Message required" }}
            error={errors.message}
            className={getInputClass(errors.message)}
            placeholder="Write your message.."
          />

          <div className="flex flex-row justify-end p-5">
            <button
              disabled={mutation.isPending || mutation.isSuccess}
              type="submit"
              className={`${
                mutation.isSuccess
                  ? "border-[#998147] text-[#998147] bg-white"
                  : "bg-[#998147] text-white"
              } border-2 hover:opacity-90 text-sm md:text-lg rounded-xl w-[100%] md:w-[20rem] h-[4rem] flex justify-center items-center gap-4 transition-all`}
            >
              {mutation.isPending ? (
                <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
              ) : mutation.isSuccess ? (
                "Sent Success"
              ) : (
                "Send Message"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Sub-components to keep code DRY
const ContactItem = ({ icon, text, link }) => (
  <div className="flex gap-1 sm:gap-6">
    <img
      className="md:w-[1.3rem] w-[1rem] h-[1rem] md:h-[1.3rem]"
      src={icon}
      alt="icon"
    />
    <a href={link} className="hover:text-[#998147] transition-colors">
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />
    </a>
  </div>
);

const SocialLink = ({ href, icon, alt }) => (
  <a
    className="hover:opacity-50 transition-opacity"
    href={href}
    target="_blank"
    rel="noreferrer"
  >
    <img
      className="w-[1.5rem] h-[1.5rem] md:w-[2.5rem] md:h-[2.5rem]"
      src={icon}
      alt={alt}
    />
  </a>
);

const FormInput = ({
  label,
  name,
  register,
  validation,
  error,
  className,
  type = "text",
  placeholder,
}) => (
  <div className="flex flex-col p-4 w-[100%] md:w-[50%]">
    <input
      type={type}
      placeholder={placeholder || label}
      className={className}
      {...register(name, validation)}
    />
    {error && <p className="text-[#f55389] text-xs mt-1">{error.message}</p>}
  </div>
);

export default Form;
